import React, { forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism-tomorrow.css';

interface RichTextEditorProps {
  value?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}

export interface RichTextEditorRef {
  insertText: (text: string) => void;
  focus: () => void;
  blur: () => void;
}

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  ({ value, onChange, placeholder, style }, ref) => {
    const [htmlContent, setHtmlContent] = useState(value || '');

    // 同步外部 value 到 htmlContent
    React.useEffect(() => {
      setHtmlContent(value || '');
    }, [value]);

    // 计算行号
    const lineNumbers = useMemo(() => {
      const lines = (htmlContent || '').split('\n');
      return lines.map((_, i) => i + 1);
    }, [htmlContent]);

    useImperativeHandle(ref, () => ({
      insertText: (text: string) => {
        const newContent = htmlContent + text;
        setHtmlContent(newContent);
        onChange?.(newContent);
      },
      focus: () => {
        // Code editor 自动获取焦点
      },
      blur: () => {
        // Code editor 自动失去焦点
      },
    }));

    const handleHtmlChange = (code: string) => {
      setHtmlContent(code);
      onChange?.(code);
    };

    return (
      <div style={style}>
        <div
          style={{
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            overflow: 'hidden',
            display: 'flex',
            backgroundColor: '#2d2d2d',
          }}
        >
          {/* 行号 */}
          <div
            style={{
              padding: '12px 0',
              textAlign: 'right',
              userSelect: 'none',
              backgroundColor: '#252525',
              color: '#858585',
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
              fontSize: 13,
              lineHeight: 1.6,
              minWidth: 40,
              borderRight: '1px solid #3c3c3c',
            }}
          >
            {lineNumbers.map(num => (
              <div key={num} style={{ paddingRight: 8 }}>
                {num}
              </div>
            ))}
          </div>
          {/* 编辑器 */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            <Editor
              value={htmlContent}
              onValueChange={handleHtmlChange}
              highlight={code => highlight(code, languages.markup, 'html')}
              padding={12}
              placeholder={placeholder || '请输入 HTML 代码...'}
              style={{
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
                fontSize: 13,
                lineHeight: 1.6,
                minHeight: 250,
                color: '#ccc',
              }}
            />
          </div>
        </div>
      </div>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;




